import type { PostHog } from 'posthog-js';
import { Router } from 'next/router';
import { useEffect, useRef, useState } from 'react';

/**
 * Lazily imports and initializes PostHog after first paint.
 * Returns the PostHog instance once ready (null until loaded).
 * This defers ~196 KB of posthog-js from the critical path.
 */
const useInitPostHog = () => {
  const [posthogClient, setPosthogClient] = useState<PostHog | null>(null);
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    // Only load PostHog in production — saves ~196 KB and avoids polluting analytics with dev data
    if (process.env.NEXT_PUBLIC_ENV !== 'prod') return;

    // Defer PostHog loading to after first paint
    const load = async () => {
      try {
        const {
          default: posthog
        } = await import('posthog-js');
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
          api_host: '/a',
          ui_host: 'https://us.posthog.com',
          // Automatically capture unhandled exceptions and send to PostHog Error Tracking
          capture_exceptions: true,
          // Enable heatmap data collection (clicks, mouse movements, scroll depth)
          enable_heatmaps: true,
          // Capture Core Web Vitals (LCP, CLS, INP, FID, TTFB) as real-user metrics
          capture_performance: {
            web_vitals: true,
            network_timing: true // Capture resource/network request timings
          },
          // Disable persistence if blocked - prevents errors
          persistence: 'localStorage+cookie',
          // Don't throw errors if PostHog is blocked
          on_request_error: error_0 => {
            // Silently handle errors when PostHog is blocked by extensions
            if (process.env.NODE_ENV === 'development') {
              // eslint-disable-next-line no-console
              console.warn('[PostHog] Request failed (possibly blocked by extension):', error_0);
            }
          },
          // Bootstrap with empty feature flags to prevent undefined errors
          bootstrap: {
            featureFlags: {}
          },
          // Capture console logs/warnings/errors in session replays for debugging
          enable_recording_console_log: true,
          // Session recording enabled
          disable_session_recording: false,
          // Add error handling for feature flags
          advanced_disable_feature_flags: false,
          advanced_disable_feature_flags_on_first_load: false,
          // Timeout for feature flag requests
          feature_flag_request_timeout_ms: 5000,
          loaded: posthogInstance => {
            if (process.env.NODE_ENV !== 'development') posthogInstance.debug(false);else posthogInstance.debug(process.env.POSTHOG_DEBUG === 'true');
          }
        });
        setPosthogClient(posthog);
        const handleRouteChange = () => {
          try {
            posthog?.capture('$pageview');
          } catch {
            // Silently fail if capture fails
          }
        };
        Router.events.on('routeChangeComplete', handleRouteChange);
      } catch (error) {
        // Silently fail if PostHog initialization fails (blocked by extension)
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[PostHog] Initialization failed (possibly blocked by extension):', error);
        }
      }
    };

    // Use requestIdleCallback to defer loading until browser is idle
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      requestIdleCallback(() => load());
    } else {
      // Fallback: load after a short delay
      setTimeout(load, 1);
    }
  }, []);
  return posthogClient;
};
export { useInitPostHog };